import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Save, RotateCcw } from "lucide-react";
import { SAMPLE_ID } from "@/lib/ticketTypes";
import { CARD_W_IN as TICKET_W_IN, CARD_H_IN as TICKET_H_IN, DEFAULT_CARD_LAYOUT as DEFAULT_LAYOUT, cardLayout, artStyle } from "@/lib/avery5871";

function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }

function BarcodePlaceholder() {
  const bars = Array.from({ length: 36 }, (_, i) => ({
    x: i * 2.7,
    w: [0.6, 1.2, 1.8][i % 3],
  }));
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-full">
      {bars.map((b, i) => (
        <rect key={i} x={b.x} y="2" width={b.w} height="26" fill="black" />
      ))}
    </svg>
  );
}

export default function LayoutEditorModal({ template, token, onClose, onSaved }) {
  const [layout, setLayout] = useState(cardLayout(template.layout));
  const [selected, setSelected] = useState("barcode");
  const [lockAspect, setLockAspect] = useState(true);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const [pxPerIn, setPxPerIn] = useState(120);

  useEffect(() => {
    const update = () => {
      if (canvasRef.current) setPxPerIn(canvasRef.current.clientWidth / TICKET_W_IN);
    };
    update();
    const ro = new ResizeObserver(update);
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, []);

  const artUrl = `${API}/print/templates/${template.id}/artwork?token=${encodeURIComponent(token)}`;

  const patch = (upd) => setLayout((prev) => ({ ...prev, ...upd }));

  const startDrag = (e, key, mode) => {
    e.stopPropagation();
    e.preventDefault();
    setSelected(key);
    const startX = e.clientX, startY = e.clientY;
    const start = { ...layout };
    const aspectRatio = start.barcode_h > 0 ? start.barcode_w / start.barcode_h : 3;

    const onMove = (mv) => {
      const dxIn = (mv.clientX - startX) / pxPerIn;
      const dyIn = (mv.clientY - startY) / pxPerIn;
      setLayout((prev) => {
        const upd = { ...prev };
        const r3 = (v) => Math.round(v * 1000) / 1000;
        if (mode === "drag") {
          if (key === "art") {
            upd.art_x = r3(clamp(start.art_x + dxIn, -TICKET_W_IN, TICKET_W_IN));
            upd.art_y = r3(clamp(start.art_y + dyIn, -TICKET_H_IN, TICKET_H_IN));
          } else if (key === "barcode") {
            upd.barcode_x = r3(clamp(start.barcode_x + dxIn, 0, TICKET_W_IN - start.barcode_w));
            upd.barcode_y = r3(clamp(start.barcode_y + dyIn, 0, TICKET_H_IN - start.barcode_h));
          } else if (key === "ticket_id") {
            upd.ticket_id_x = r3(clamp(start.ticket_id_x + dxIn, 0, TICKET_W_IN - 0.3));
            upd.ticket_id_y = r3(clamp(start.ticket_id_y + dyIn, 0, TICKET_H_IN - 0.1));
          } else if (key === "guest") {
            upd.guest_x = r3(clamp(start.guest_x + dxIn, 0, TICKET_W_IN - 0.3));
            upd.guest_y = r3(clamp(start.guest_y + dyIn, 0, TICKET_H_IN - 0.1));
          }
        } else if (mode === "resize" && key === "barcode") {
          let newW = clamp(start.barcode_w + dxIn, 0.5, TICKET_W_IN - start.barcode_x);
          let newH = clamp(start.barcode_h + dyIn, 0.15, TICKET_H_IN - start.barcode_y);
          if (lockAspect) {
            // Constrain to aspect ratio
            const byWidth = { w: newW, h: newW / aspectRatio };
            const byHeight = { w: newH * aspectRatio, h: newH };
            const useWidth = Math.abs(byWidth.h - start.barcode_h) < Math.abs(byHeight.w - start.barcode_w);
            const r = useWidth ? byWidth : byHeight;
            newW = clamp(r.w, 0.5, TICKET_W_IN - start.barcode_x);
            newH = clamp(r.h, 0.15, TICKET_H_IN - start.barcode_y);
          }
          upd.barcode_w = r3(newW);
          upd.barcode_h = r3(newH);
        }
        return upd;
      });
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/print/templates/${template.id}`, { layout });
      toast.success("Layout saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const pctX = (v) => (v / TICKET_W_IN) * 100;
  const pctY = (v) => (v / TICKET_H_IN) * 100;

  const sampleId = SAMPLE_ID[template.ticket_type] || "VIP-0001";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="layout-editor">
      <div className="bg-[#0B1120] border border-slate-700 rounded-2xl w-full max-w-6xl my-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Configure Layout</div>
            <div className="text-white font-bold text-lg">{template.ticket_type} · v{template.version}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="editor-close" aria-label="Close">
            <X />
          </button>
        </div>

        <div className="p-5 grid lg:grid-cols-[1fr_340px] gap-5">
          <div>
            <div
              ref={canvasRef}
              className="relative w-full rounded-lg overflow-hidden bg-white border border-slate-700 select-none touch-none"
              style={{ aspectRatio: `${TICKET_W_IN} / ${TICKET_H_IN}` }}
              onPointerDown={() => setSelected(null)}
            >
              <img src={artUrl} alt="" style={artStyle(layout)} draggable={false}
                onPointerDown={(e) => startDrag(e, "art", "drag")}
                className={`cursor-move ${selected === "art" ? "outline outline-2 outline-fuchsia-500" : ""}`}
                data-testid="overlay-artwork" />

              {/* Barcode overlay */}
              <div
                onPointerDown={(e) => startDrag(e, "barcode", "drag")}
                className={`absolute cursor-move flex items-center justify-center overflow-hidden ${selected === "barcode" ? "ring-2 ring-orange-500" : "ring-1 ring-orange-400/70"}`}
                style={{
                  left: `${pctX(layout.barcode_x)}%`,
                  top: `${pctY(layout.barcode_y)}%`,
                  width: `${pctX(layout.barcode_w)}%`,
                  height: `${pctY(layout.barcode_h)}%`,
                  background: layout.barcode_white_bg ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.30)",
                }}
                data-testid="overlay-barcode"
              >
                <div className="w-[92%] h-[80%]"><BarcodePlaceholder /></div>
                <div
                  onPointerDown={(e) => startDrag(e, "barcode", "resize")}
                  className="absolute -right-1 -bottom-1 w-4 h-4 bg-orange-500 border-2 border-white rounded-sm cursor-se-resize"
                  data-testid="resize-barcode"
                />
              </div>

              {/* Ticket ID overlay */}
              <div
                onPointerDown={(e) => startDrag(e, "ticket_id", "drag")}
                className={`absolute cursor-move px-1 whitespace-nowrap font-bold text-black leading-none ${selected === "ticket_id" ? "ring-2 ring-emerald-500" : "ring-1 ring-emerald-400/70"}`}
                style={{
                  left: `${pctX(layout.ticket_id_x)}%`,
                  top: `${pctY(layout.ticket_id_y)}%`,
                  fontSize: `${(layout.ticket_id_size / 72) * pxPerIn}px`,
                  background: "rgba(255,255,255,0.6)",
                }}
                data-testid="overlay-ticket-id"
              >
                {sampleId}
              </div>

              {/* Guest name overlay */}
              {layout.show_guest_name && (
                <div
                  onPointerDown={(e) => startDrag(e, "guest", "drag")}
                  className={`absolute cursor-move px-1 whitespace-nowrap text-black leading-none ${selected === "guest" ? "ring-2 ring-sky-500" : "ring-1 ring-sky-400/70"}`}
                  style={{
                    left: `${pctX(layout.guest_x)}%`,
                    top: `${pctY(layout.guest_y)}%`,
                    fontSize: `${(layout.guest_size / 72) * pxPerIn}px`,
                    background: "rgba(255,255,255,0.6)",
                  }}
                  data-testid="overlay-guest"
                >
                  Sample Guest
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Avery 5871 card: 3.5" × 2" · Drag the artwork, barcode or ID to reposition · Drag the orange corner to resize the barcode
            </div>
          </div>

          <div className="space-y-3">
            <div onClick={() => setSelected("art")}
              className={`rounded-lg bg-[#131D33] border p-3 cursor-pointer ${selected === "art" ? "border-fuchsia-500/50" : "border-slate-800"}`} data-testid="artwork-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-widest text-fuchsia-400">Artwork</div>
                <button onClick={() => patch({ art_x: 0, art_y: 0, art_scale: 1 })} className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-white" data-testid="art-fit-width">Fit width</button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[["art_x", "X (in)", 0.01], ["art_y", "Y (in)", 0.01], ["art_scale", "Scale ×", 0.01]].map(([k, lbl, st]) => (
                  <div key={k}>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">{lbl}</Label>
                    <Input type="number" step={st} min={k === "art_scale" ? 0.2 : undefined} max={k === "art_scale" ? 3 : undefined}
                      value={layout[k]} onChange={(e) => patch({ [k]: k === "art_scale" ? clamp(Number(e.target.value), 0.2, 3) : Number(e.target.value) })}
                      className="bg-[#0B1120] border-slate-700 text-white h-9" data-testid={`inp-${k}`} />
                  </div>
                ))}
              </div>
              <input type="range" min="0.2" max="3" step="0.01" value={layout.art_scale}
                onChange={(e) => patch({ art_scale: Number(e.target.value) })}
                className="w-full mt-2 accent-fuchsia-500" data-testid="art-scale-slider" />
              <p className="text-[10px] text-slate-500 mt-1">Scaled proportionally — never stretched. Anything outside the card is trimmed.</p>
            </div>

            {/* Barcode panel */}
            <div onClick={() => setSelected("barcode")}
              className={`rounded-lg bg-[#131D33] border p-3 cursor-pointer ${selected === "barcode" ? "border-orange-500/50" : "border-slate-800"}`}>
              <div className="text-xs font-bold uppercase tracking-widest mb-2 text-orange-400">Barcode</div>
              <div className="grid grid-cols-2 gap-2">
                {["barcode_x", "barcode_y", "barcode_w", "barcode_h"].map((k) => (
                  <div key={k}>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">{k.replace("barcode_", "").toUpperCase()} (in)</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={layout[k]}
                      onChange={(e) => patch({ [k]: Number(e.target.value) })}
                      className="bg-[#0B1120] border-slate-700 text-white h-9"
                      data-testid={`inp-${k}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-2 text-xs text-slate-300">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={lockAspect} onChange={(e) => setLockAspect(e.target.checked)} data-testid="lock-aspect" />
                  Lock aspect ratio when resizing
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!layout.barcode_white_bg}
                    onChange={(e) => patch({ barcode_white_bg: e.target.checked })}
                    data-testid="barcode-white-bg" />
                  White background behind barcode (recommended)
                </label>
              </div>
            </div>

            {/* Ticket ID panel */}
            <div onClick={() => setSelected("ticket_id")}
              className={`rounded-lg bg-[#131D33] border p-3 cursor-pointer ${selected === "ticket_id" ? "border-emerald-500/50" : "border-slate-800"}`}>
              <div className="text-xs font-bold uppercase tracking-widest mb-2 text-emerald-400">Ticket ID</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500">X (in)</Label>
                  <Input type="number" step="0.01" min="0" value={layout.ticket_id_x}
                    onChange={(e) => patch({ ticket_id_x: Number(e.target.value) })}
                    className="bg-[#0B1120] border-slate-700 text-white h-9" data-testid="inp-ticket_id_x" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500">Y (in)</Label>
                  <Input type="number" step="0.01" min="0" value={layout.ticket_id_y}
                    onChange={(e) => patch({ ticket_id_y: Number(e.target.value) })}
                    className="bg-[#0B1120] border-slate-700 text-white h-9" data-testid="inp-ticket_id_y" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500">pt</Label>
                  <Input type="number" step="0.5" min="5" max="24" value={layout.ticket_id_size}
                    onChange={(e) => patch({ ticket_id_size: Number(e.target.value) })}
                    className="bg-[#0B1120] border-slate-700 text-white h-9" data-testid="inp-ticket_id_size" />
                </div>
              </div>
            </div>

            {/* Guest name panel */}
            <div className={`rounded-lg bg-[#131D33] border p-3 ${selected === "guest" ? "border-sky-500/50" : "border-slate-800"}`}>
              <label className="flex items-center justify-between">
                <span className="text-xs text-sky-300 font-bold uppercase tracking-widest">Guest Name</span>
                <Switch checked={!!layout.show_guest_name}
                  onCheckedChange={(v) => patch({ show_guest_name: v })}
                  data-testid="guest-toggle" />
              </label>
              {layout.show_guest_name && (
                <div className="mt-3 grid grid-cols-3 gap-2" onClick={() => setSelected("guest")}>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">X (in)</Label>
                    <Input type="number" step="0.01" min="0" value={layout.guest_x}
                      onChange={(e) => patch({ guest_x: Number(e.target.value) })}
                      className="bg-[#0B1120] border-slate-700 text-white h-9" data-testid="inp-guest_x" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">Y (in)</Label>
                    <Input type="number" step="0.01" min="0" value={layout.guest_y}
                      onChange={(e) => patch({ guest_y: Number(e.target.value) })}
                      className="bg-[#0B1120] border-slate-700 text-white h-9" data-testid="inp-guest_y" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">pt</Label>
                    <Input type="number" step="0.5" min="5" max="24" value={layout.guest_size}
                      onChange={(e) => patch({ guest_size: Number(e.target.value) })}
                      className="bg-[#0B1120] border-slate-700 text-white h-9" data-testid="inp-guest_size" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button variant="outline"
                onClick={() => setLayout({ ...DEFAULT_LAYOUT, show_guest_name: layout.show_guest_name })}
                className="bg-slate-900 border-slate-700 text-slate-200 flex-1" data-testid="reset-layout">
                <RotateCcw size={14} className="mr-1" /> Reset
              </Button>
              <Button onClick={save} disabled={saving}
                className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold flex-1"
                data-testid="save-layout">
                <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save Layout"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
